import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";

const faqs: FAQItem[] = [
  {
    question: "What are the signs my boss is depressed, not just stressed?",
    answer:
      "The distinction is persistence and pattern. Decision paralysis on things that used to take ten minutes. The same concern raised in meeting after meeting without resolution. Cancelling meetings that involve people. Emails at 3 a.m. Irritability that isn't like them. Forgetting things they'd never have forgotten. And the most telling sign: flatness — the absence of reaction to news, good or bad, that would have mattered a year ago. If several of these have persisted for more than a few weeks and are getting worse, it's very likely a clinical condition, not a rough patch.",
  },
  {
    question:
      "Is it my place to say something to my boss about their mental health?",
    answer:
      "It might be. If your executive has a spouse, close friend, chief of staff, or board member who's already aware and engaged, your role may be to support that person's efforts. But often there is no such person, or the people who should be seeing it are too far away or too deferential. If you've looked around and realized you might be the only one who sees the whole picture, then yes, it's your place — not because you're obligated, but because silence has a cost, and you're watching it accrue.",
  },
  {
    question:
      "What's the difference between grief and depression after a trauma?",
    answer:
      "Grief comes in waves, it's clearly about the loss, it responds to comfort, and it gradually makes room for a new life. Depression doesn't fluctuate — low mood most of the day, nearly every day, for two weeks or more. It spreads beyond the trigger to everything. And it comes with the full symptom set: insomnia, appetite change, fatigue, trouble concentrating, and sometimes thoughts of not wanting to be here. A trauma that triggers this didn't cause depression out of nothing; it uncovered a vulnerability that was already there.",
  },
  {
    question: "What is SAINT and why is it relevant to an executive?",
    answer:
      "SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, accelerated form of TMS developed at Stanford's Brain Stimulation Lab for adults whose depression hasn't responded adequately to antidepressants. It's five days instead of six weeks, no anesthesia, nothing in the bloodstream, and in clinical trials 79 percent of people who had failed medication reached remission. For an executive who can't disappear for two months of daily appointments, the schedule is what makes it possible. Neuro Wellness TMS Centers of America is a Verified SAINT Provider.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "How to Tell Your Boss You're Worried About Them (Without Losing Your Job or Their Trust)",
    href: "/how-to-tell-your-boss-youre-worried",
    description:
      "The second article in this series: what to say, what not to say, and how to raise it carefully.",
  },
  {
    title: "What to Have Ready: A Resource Guide for the Executive Assistant",
    href: "/what-to-have-ready-executive-assistant-resource-guide",
    description:
      "The third article: the treatment options, which one fits an executive's life, and how to make the first step happen.",
  },
  {
    title: "The Executive Who Can't Afford Six Weeks",
    href: "/the-executive-who-cant-afford-six-weeks",
    description:
      "Why high performers get stuck in partial treatment — and what changes the calculation.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
];

export default function ExecutiveAssistantWatchingBossFallApart() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "You're Watching Your Boss Fall Apart. Here's What You're Seeing",
        description:
          "For executive assistants supporting a leader through trauma and depression: what the symptoms look like from your seat, why the spiral has a mechanism, and what you're actually witnessing.",
        canonical: "/watching-your-boss-fall-apart",
        keywords:
          "executive assistant boss depression, leader mental health workplace, trauma depression executive, SAINT TMS, depression symptoms workplace, decision paralysis depression, executive mental health, anxiety depression leader, what to do when boss is depressed",
      }}
      badge="For Executive Assistants"
      heroTitle="You're the Executive Assistant. You're Watching Your Boss Fall Apart. Here's What You're Actually Seeing."
      heroSubtitle="You know them better than almost anyone. And something happened. Since then, you've been watching a person you respect come apart in slow motion. You're not their doctor, their spouse, or their friend, exactly. But you're the one who sees it. And you don't know what to do with what you're seeing."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                You know them better than almost anyone. Better, in some ways,
                than their family. You know what time they really get in, what
                their voice sounds like when a call went badly, how many
                meetings they've quietly canceled this month, and which ones
                they used to never miss.
              </p>
              <p>
                And something happened. A death, a diagnosis, a divorce, a loss
                so large it doesn't have a clean name. Since then, you've been
                watching a person you respect come apart in slow motion. Not
                dramatically. Incrementally. A missed deadline here. A decision
                that would have taken them ten minutes a year ago sitting
                untouched for a week. A shortness with people that isn't like
                them. Emails at 3 a.m. Cancellations with no reason. The same
                problem being discussed over and over without moving.
              </p>
              <p>
                You're not their doctor, their spouse, or their friend, exactly.
                But you're the one who sees it. And you don't know what to do
                with what you're seeing.
              </p>
              <p>
                This article is the first of three. It's about understanding
                what's happening. The next two are about how to raise it and
                what resources exist.
              </p>
            </div>
          ),
        },
        {
          heading: "What trauma does, and what comes after",
          body: (
            <div className="space-y-5">
              <p>
                When a person goes through something devastating, the first
                phase is acute. Shock, grief, disruption. Everyone expects that,
                and most workplaces make room for it, briefly.
              </p>
              <p>
                What's less understood is what happens in the months after. For
                some people, the acute phase resolves and they rebuild. For
                others, it doesn't resolve. It settles into something chronic: a
                persistent low mood, an inability to feel pleasure or interest
                in anything, exhaustion that sleep doesn't touch, a mind that
                either won't focus or won't stop looping. That's the transition
                from grief to major depressive disorder, and it's extremely
                common after significant trauma. Anxiety usually comes with it:
                the racing thoughts, the dread, the inability to make decisions
                because every option feels catastrophic.
              </p>
              <p>
                The "spiral" you're witnessing has a mechanism. Depression
                suppresses the part of the brain that regulates mood,
                motivation, and executive function — the same region a leader
                relies on to prioritize, decide, and act. Anxiety floods it with
                threat signals. The result is a person who can't get out of the
                loop because the loop is being generated by the very system
                they'd use to break it.
              </p>
            </div>
          ),
        },
        {
          heading: "What you're probably seeing",
          body: (
            <div className="space-y-5">
              <p>
                From your seat, the symptoms of depression and anxiety don't
                look clinical. They look operational:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Decision paralysis.</strong> Things that need a yes or
                  no sit for days. When pushed, they re-open questions that were
                  already settled.
                </li>
                <li>
                  <strong>Loops.</strong> The same concern, raised in meeting
                  after meeting, never resolved. Ruminating out loud.
                </li>
                <li>
                  <strong>Withdrawal.</strong> Cancelling the meetings that
                  involve people. Keeping the ones that don't. Fewer calls,
                  shorter ones.
                </li>
                <li>
                  <strong>Time distortion.</strong> Late in, late out, or in
                  absurdly early. Emails at hours that suggest they aren't
                  sleeping.
                </li>
                <li>
                  <strong>Irritability.</strong> Snapping at people who've never
                  been snapped at. Then, sometimes, over-apologizing.
                </li>
                <li>
                  <strong>Cognitive slippage.</strong> Forgetting things they'd
                  never have forgotten. Asking you to repeat things. Reading the
                  same document multiple times.
                </li>
                <li>
                  <strong>Physical signs.</strong> Weight change. Looking
                  exhausted. Headaches. "I'm fine, just tired," said constantly.
                </li>
                <li>
                  <strong>Drinking, or something like it.</strong> More than
                  before. Or a new reliance on something to sleep, to focus, to
                  cope.
                </li>
                <li>
                  <strong>Flatness.</strong> The thing that scares you most. The
                  absence of reaction to news, good or bad, that would have
                  mattered a year ago.
                </li>
              </ul>
              <p>
                If several of these have persisted for more than a few weeks,
                and they're getting worse rather than better, what you're seeing
                isn't a rough patch. It's very likely a clinical condition.
              </p>
            </div>
          ),
        },
        {
          heading: "What you're not seeing",
          body: (
            <div className="space-y-5">
              <p>
                You may not see the worst of it, because you see them in the one
                environment where they're still performing. Executives are
                extraordinarily good at holding the mask up at work. What you're
                seeing is the leakage around the edges. The full picture at home
                may be considerably darker.
              </p>
              <p>
                That's worth knowing because it changes the urgency. If it looks
                bad from where you sit, it's probably worse than it looks.
              </p>
            </div>
          ),
        },
        {
          heading: "What it isn't",
          body: (
            <div className="space-y-5">
              <p>
                It isn't a character failure. It isn't weakness. It isn't
                "burnout" that a vacation will fix, though people will keep
                suggesting one. It isn't something they can decide their way out
                of, because the deciding apparatus is what's impaired.
              </p>
              <p>
                And it isn't your job to fix it. Your job, if you choose to take
                it on, is much smaller and much more useful: to see clearly, to
                say something carefully, and to have a resource ready when
                they're willing to hear it.
              </p>
            </div>
          ),
        },
        {
          heading: "Why this matters more than it seems",
          body: (
            <div className="space-y-5">
              <p>
                Untreated depression in a leader doesn't stay contained. It
                propagates. Decisions don't get made, or get made badly. People
                below them start compensating, then resenting, then leaving.
                Board members and partners notice. The organization begins to
                reflect the state of the person at the top.
              </p>
              <p>
                You may be the only person positioned to interrupt that, because
                you may be the only person who sees the whole pattern and has a
                reason to be in the room.
              </p>
            </div>
          ),
        },
        {
          heading: "What comes next",
          body: (
            <div className="space-y-5">
              <p>
                The second article in this series is about how to raise it: what
                to say, what not to say, and how to do it without overstepping a
                role that depends on discretion.
              </p>
              <p>
                The third is about what to have ready when they say yes: the
                treatment options that exist now, including one that takes five
                days rather than months, and how to get them into a
                consultation.
              </p>
              <p>
                If you want to skip ahead, or if things feel urgent, the number
                is below. Neuro Wellness TMS Centers of America is a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                offering private outpatient care with a dedicated care team and
                weekend availability. You can call on their behalf to ask
                questions. You cannot schedule treatment for them, and you
                shouldn't try; that has to be their decision. But you can find
                out what's possible so that when the moment comes, you're not
                starting from zero.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p className="font-semibold text-primary">
                Ask questions or schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If your executive says anything that suggests they're thinking
                about ending their life, treat it as an emergency. Encourage
                them to call or text 988 (Suicide &amp; Crisis Lifeline), or
                call it yourself for guidance on how to help. This article is
                for educational purposes and is not a substitute for individual
                medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
